'use client';

import { SessionData } from '@auth0/nextjs-auth0/types';
import { saveLocation, saveSelectedPlan } from 'actions/Database';
import { useCart } from 'hooks/useCart';
import { useRecommendation } from 'hooks/useRecommendation';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { addToCart, savePlan } from 'slices/cartSlice';
import { setDynamicValuesAll } from 'slices/dynamicSlice';
import { setLocation } from 'slices/locationSlice';
import { selectReadyForRecommendation } from 'slices/selectors';
import { selectUser } from 'slices/userSlice';
import { MedicareApiPlan } from 'types/api';
import {
  DEFAULT_PLAN_YEAR,
  planYearForShoppingPreference,
} from 'lib/planYears';
import { readSessionItem, removeSessionItem } from 'lib/safeSessionStorage';
import { setSelectedPlanYear } from 'slices/availabilitySlice';

type AppInitializerProps = {
  session: SessionData | null;
};

const AppInitializer = ({ session }: AppInitializerProps) => {
  const user = useAppSelector(selectUser);
  const isReady = useAppSelector(selectReadyForRecommendation);
  const dispatch = useAppDispatch();
  const recommendation = useRecommendation();
  const updateCart = useCart();
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (user.id && isReady && user.recommendation_type) {
      (async () => {
        await recommendation();
        updateCart(user.recommendation_type as string);
      })();
    }
  }, [isReady, user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return-user resume — redirect to /bif on login if last_slug is set.
  // Skip if there's a pending save-plan action so the user lands on the details page instead.
  useEffect(() => {
    const hasPendingSave = !!readSessionItem('pendingSavePlan');
    if (
      searchParams.get('loginSuccess') === 'true' &&
      user.last_slug &&
      !hasPendingSave
    ) {
      router.push('/bif?loginSuccess=true');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // sessionStorage migration — runs once on mount if logged in
  useEffect(() => {
    if (!session) return;

    (async () => {
      // Apply this before any awaited migration. Browse, cart, and saved
      // plans read the shopping-year preference on the client, and it is
      // not in the SSR preload.
      const dynamicValues = readSessionItem('dynamicValues');
      if (dynamicValues) {
        const restored = JSON.parse(dynamicValues) as {
          preferredShoppingYear?: string;
        };
        dispatch(setDynamicValuesAll(restored));
        // /wcy stores the shopping year in sessionStorage and on
        // availability.selectedPlanYear. Only the preference survives a
        // reload, so Med Supp routing would otherwise check the default
        // year (2027) after the member chose this year (2026).
        if (restored.preferredShoppingYear) {
          dispatch(
            setSelectedPlanYear(
              planYearForShoppingPreference(restored.preferredShoppingYear)
            )
          );
        }
      }

      const location = readSessionItem('cachedLocation');
      if (location) {
        const updatedUser = await saveLocation(JSON.parse(location));
        dispatch(
          setLocation({
            countyFips: updatedUser.county_fips as string,
            countyName: updatedUser.county_name as string,
            state: updatedUser.state as string,
            zipcode: updatedUser.zipcode as string,
          })
        );
        removeSessionItem('cachedLocation');
      }

      const cartPlans = readSessionItem('cart');
      if (cartPlans) {
        for (const plan of JSON.parse(cartPlans) as MedicareApiPlan[]) {
          dispatch(addToCart(plan));
          await saveSelectedPlan(
            plan,
            'cart',
            plan.planYear ?? DEFAULT_PLAN_YEAR
          );
        }
        removeSessionItem('cart');
      }

      const pendingSavePlan = readSessionItem('pendingSavePlan');
      if (pendingSavePlan) {
        const plan = JSON.parse(pendingSavePlan) as MedicareApiPlan;
        dispatch(savePlan(plan));
        await saveSelectedPlan(
          plan,
          'saved',
          plan.planYear ?? DEFAULT_PLAN_YEAR
        );
        removeSessionItem('pendingSavePlan');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default AppInitializer;
